import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wrench, RefreshCw, Hammer, HelpCircle, ToggleLeft, Plug, Camera, Upload, X,
  XCircle, AlertCircle, AlertTriangle, Zap, Flame, Waves, Ban,
  ArrowRight, ArrowLeft, CalendarDays, CheckCircle2, Circle, Plus, Minus,
} from "lucide-react";
import config from "../../config";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD     = 3;

const WORK_TYPE_ICONS = { replace: RefreshCw, install: Hammer, consultation: HelpCircle };
const ITEM_ICONS = { switch: ToggleLeft, socket: Plug };
const PROBLEM_ICONS = {
  not_working: XCircle, loose: AlertCircle, sparking: Zap, heating: Flame, burning: Flame,
  no_power: Ban, intermittent: RefreshCw, mcb_tripping: AlertTriangle, other: HelpCircle,
};

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const STEPS = [
  { key: "service",  label: "Service",     sub: "Install, replacement or consultation" },
  { key: "item",     label: "Item",        sub: "Rating, quantity & material"          },
  { key: "schedule", label: "Date & Time", sub: "Book your visit"                       },
];
const ITEM_SUBSTEPS = ["item", "rating", "quantity", "material", "photo", "problem", "mode"];

const SwitchSocketBookingPage = () => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const service = useMemo(
    () => config.SERVICE_CONFIG?.technical?.services?.["electrical-services"]?.types?.find((t) => t.value === "switch-socket"),
    []
  );

  const [step, setStep] = useState("service");
  const [workTypeKey, setWorkTypeKey] = useState(null);
  const [item, setItem] = useState(null); // subCategory: switch | socket
  const [selectedItems, setSelectedItems] = useState([]); // [{value,label,ratingKey,basePrice,quantity,image}]
  const [materialOption, setMaterialOption] = useState("clicknido");
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [problemKey, setProblemKey] = useState(null);
  const [applianceDetails, setApplianceDetails] = useState("");
  const [bookingMode, setBookingMode] = useState(null); // instant | schedule | requirement

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const stepIndex = STEPS.findIndex((s) => s.key === (ITEM_SUBSTEPS.includes(step) ? "item" : step));
  const workType = service?.workTypes?.find((wt) => wt.key === workTypeKey) || null;
  const isConsultation = workTypeKey === "consultation";

  const totalQty = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  // ── Pricing (per the switch-socket pricing doc — see pricing.js comment).
  // Deliberately NOT city-adjusted; this service uses its own flat formula.
  const materialFactor = materialOption === "clicknido" ? 1 : (service?.customerMaterialFactor || 0.6);
  const serviceAmountFor = (i) => {
    if (typeof i.basePrice !== "number") return 0; // 40A+/Not Sure — inspection only, no fixed amount yet
    const effectiveBase = i.basePrice * materialFactor;
    const additionalPiece = effectiveBase * 0.81;
    return effectiveBase + Math.max(0, i.quantity - 1) * additionalPiece;
  };
  const totalServiceAmount = Number(selectedItems.reduce((sum, i) => sum + serviceAmountFor(i), 0).toFixed(2));
  const consultationFee = service?.consultationFee || 90;
  // Install/Replacement pay the itemized Service Amount; Consultation pays
  // only the flat Consultation Charge — the two are mutually exclusive, not
  // stacked (see the supplied spec's separate "Price" vs "Consultation
  // Charge" review screens).
  const subtotal = Number((isConsultation ? consultationFee : totalServiceAmount).toFixed(2));
  const platformFee = Number((subtotal * 0.06).toFixed(2));
  const gst = Number((subtotal * 0.06).toFixed(2));
  const finalPayable = Number((subtotal * 1.12).toFixed(2));

  const hasHighLoadOrNotSure = selectedItems.some((i) => i.ratingKey === "notsure");
  const forcedRequirement = hasHighLoadOrNotSure;
  const instantEligible = isConsultation
    ? true
    : (!forcedRequirement && totalQty === 1 && workTypeKey !== "install" &&
       selectedItems.every((i) => i.ratingKey === "normal" || i.ratingKey === "heavy"));

  const chooseWorkType = (key) => {
    setWorkTypeKey(key);
    setItem(null);
    if (key === "consultation") {
      setStep("problem");
    } else {
      setStep("item");
    }
  };

  // Every switch/socket type is included in the service — the customer just
  // picks Switch or Socket, not the exact type name; the electrician handles
  // whichever type it turns out to be on-site.
  const chooseItem = (it) => {
    setItem(it);
    setStep("rating");
  };

  const addResolvedItem = (itemObj, rating) => {
    const composedValue = `switchsocket_${workTypeKey}_${itemObj.key}_${rating.value}`;
    const label = `${itemObj.label} — ${rating.label}`;
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.value === composedValue);
      if (exists) {
        return prev.map((i) => (i.value === composedValue ? { ...i, quantity: Math.min(i.quantity + 1, 10) } : i));
      }
      return [...prev, {
        value: composedValue, label, ratingKey: rating.value, basePrice: rating.basePrice,
        quantity: 1, image: service?.image,
      }];
    });
  };

  const pickRating = (rating) => {
    addResolvedItem(item, rating);
    setStep("quantity");
  };

  const updateItemQty = (value, delta) => {
    setSelectedItems((prev) =>
      prev.map((i) => (i.value === value ? { ...i, quantity: Math.max(0, Math.min(i.quantity + delta, 10)) } : i)).filter((i) => i.quantity > 0)
    );
  };
  const removeItem = (value) => setSelectedItems((prev) => prev.filter((i) => i.value !== value));
  const addAnother = () => { setItem(null); setStep("item"); };

  const confirmQuantity = () => {
    if (selectedItems.length === 0) return;
    setStep("material");
  };

  const handlePhotoFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter(String(reader.result || ""));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const goBack = () => {
    if (step === "mode") { setStep(isConsultation ? "problem" : "photo"); return; }
    if (step === "problem") { setWorkTypeKey(null); setStep("service"); return; }
    if (step === "photo") { setStep("material"); return; }
    if (step === "material") { setStep("quantity"); return; }
    if (step === "quantity") { setStep("item"); return; }
    if (step === "rating") { setStep("item"); return; }
    if (step === "item") { setWorkTypeKey(null); setItem(null); setStep("service"); return; }
    if (step === "schedule") { setStep(hasHighLoadOrNotSure ? "photo" : "mode"); return; }
    navigate("/schedule-booking/technical");
  };

  const afterPhotoContinue = () => {
    if (forcedRequirement) {
      setBookingMode("requirement");
      goToSchedule("requirement");
    } else {
      setStep("mode");
    }
  };

  const chooseBookingMode = (mode) => {
    setBookingMode(mode);
    if (mode === "instant") {
      handleInstantCheckout();
    } else {
      goToSchedule(mode);
    }
  };

  const goToSchedule = async (mode) => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const bookingTitle = () => {
    if (isConsultation) {
      const problemLabel = problemKey ? service.problems.find((p) => p.key === problemKey)?.label : null;
      return `Switch & Socket Consultation${problemLabel ? ` — ${problemLabel}` : ""}`;
    }
    const parts = selectedItems.map((i) => `${i.label} × ${i.quantity}`);
    return `${workType?.label || ""} Switch & Socket — ${parts.join(", ")}`.trim();
  };

  const bookingShort = () => {
    if (isConsultation) {
      return [
        problemKey ? `Issue: ${service.problems.find((p) => p.key === problemKey)?.label}` : null,
        applianceDetails.trim() || null,
        photoDataUrl ? "Photo attached" : null,
        `Consultation ₹${consultationFee.toFixed(2)}`,
      ].filter(Boolean).join(" · ");
    }
    return [
      materialOption === "clicknido" ? "ClickNido Material" : "Customer Material",
      photoDataUrl ? "Photo attached" : null,
    ].filter(Boolean).join(" · ");
  };

  const handleInstantCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const title = bookingTitle();
      const cartItem = {
        id: `switchsocket-instant-${Date.now()}`,
        title, name: title,
        short: bookingShort(),
        subtitle: isConsultation ? "Consultation" : `${totalQty} unit${totalQty > 1 ? "s" : ""}`,
        category: "Switch & Socket",
        price: finalPayable,
        image: selectedItems[0]?.image || service?.image,
        qty: 1,
        booking_type: workTypeKey,
        material_source: isConsultation ? null : materialOption,
        consultation_charge: isConsultation ? consultationFee : 0,
        problem_key: isConsultation ? problemKey : null,
      };
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...(Array.isArray(existing) ? existing : []), cartItem]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      if (isUserLoggedIn()) {
        navigate("/checkout-instant/summary", { state: { cartItems: [cartItem] } });
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/checkout-instant/summary", cartItems: [cartItem] } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedDate || !selectedTime) return;
    setCheckoutLoading(true);
    try {
      const title = bookingTitle();
      const cartItem = {
        id: `switchsocket-${bookingMode}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
        title, name: title,
        short: bookingShort(),
        subtitle: isConsultation ? "Consultation" : `${totalQty} unit${totalQty > 1 ? "s" : ""}`,
        category: "Switch & Socket",
        price: finalPayable,
        image: selectedItems[0]?.image || service?.image,
        qty: 1,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: "technical",
        booking_type: workTypeKey,
        material_source: isConsultation ? null : materialOption,
        consultation_charge: isConsultation ? consultationFee : 0,
        problem_key: isConsultation ? problemKey : null,
      };
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItem.id) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, cartItem]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      // Back to the shared technical-services cart-building page (already
      // written to the shared cart above) instead of straight to checkout,
      // so other services can still be added before paying once for all.
      if (isUserLoggedIn()) {
        navigate("/schedule-booking/technical");
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/schedule-booking/technical" } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!service) {
    return <div style={S.page}><div style={S.container}><div style={S.errorBox}>Switch & Socket service is not configured.</div></div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/schedule-booking/technical" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>Switch &amp; Socket</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><CalendarDays size={13} /> SWITCH &amp; SOCKET BOOKING</div>
          <h1 style={S.heroTitle}>Book Switch &amp; Socket Install, Replacement or Consultation</h1>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── STEP 1: What do you need? ───────────────────────────────────── */}
        {step === "service" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What do you need?</h2>
            <p style={S.sectionSub}>Know exactly what you need? Choose Install or Replacement. Not sure? Choose Consultation.</p>
            <div style={S.workTypeGrid}>
              {service.workTypes.map((wt) => {
                const Icon = WORK_TYPE_ICONS[wt.key] || Wrench;
                return (
                  <button key={wt.key} type="button" style={S.workTypeCard} onClick={() => chooseWorkType(wt.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={22} color="#16a34a" /></div>
                    <div style={S.workTypeBody}>
                      <div style={S.workTypeLabel}>{wt.label}</div>
                      {wt.description && <div style={S.workTypeDesc}>{wt.description}</div>}
                    </div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP 2: Select Item (Switch / Socket) ───────────────────────── */}
        {step === "item" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Select Item — {workType?.label}</h2>
            <p style={S.sectionSub}>Just switch or socket — every type is included in our service, no need to pick the exact one</p>
            <div style={S.workTypeGrid}>
              {service.subCategories.map((sub) => {
                const Icon = ITEM_ICONS[sub.key] || ToggleLeft;
                return (
                  <button key={sub.key} type="button" style={S.workTypeCard} onClick={() => chooseItem(sub)}>
                    <div style={S.workTypeIconWrap}><Icon size={20} color="#16a34a" /></div>
                    <div style={S.workTypeLabel}>{sub.label}</div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>

            {selectedItems.length > 0 && (
              <div style={S.addedBox}>
                <div style={S.addedTitle}>Added to this booking</div>
                {selectedItems.map((sel) => (
                  <div key={sel.value} style={S.addedRow}>
                    <span>{sel.label} × {sel.quantity}</span>
                    <button type="button" style={S.addedRemoveBtn} onClick={() => removeItem(sel.value)}><X size={13} /></button>
                  </div>
                ))}
                <button type="button" style={S.continueBtn} onClick={() => setStep("quantity")}>
                  Continue ({totalQty}) <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3: Rating ───────────────────────────────────────────────── */}
        {step === "rating" && item && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{item.label} — Select Rating</h2>
            <p style={S.sectionSub}>Normal, Heavy or Not Sure — the electrician verifies the exact rating, wiring and earthing before proceeding</p>
            <div style={S.workTypeGrid}>
              {service.ratings.map((r) => (
                <button key={r.value} type="button" style={S.workTypeCard} onClick={() => pickRating(r)}>
                  <div style={S.workTypeIconWrap}><Zap size={20} color="#16a34a" /></div>
                  <div style={S.workTypeBody}>
                    <div style={S.workTypeLabel}>{r.label}</div>
                    {typeof r.basePrice === "number" && <div style={S.typePrice}>₹{r.basePrice} first piece</div>}
                    {r.requirement && <div style={S.typeNote}>Requires inspection</div>}
                  </div>
                  <ArrowRight size={16} color="#9ca3af" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 4: Quantity ─────────────────────────────────────────────── */}
        {step === "quantity" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Quantity</h2>
            <p style={S.sectionSub}>How many of each?</p>
            {selectedItems.map((sel) => (
              <div key={sel.value} style={S.qtyLineRow}>
                <span style={S.qtyLineLabel}>{sel.label}</span>
                <div style={S.qtyRow}>
                  <button type="button" style={S.qtyBtn} onClick={() => updateItemQty(sel.value, -1)}><Minus size={13} /></button>
                  <span style={S.qtyText}>{sel.quantity}</span>
                  <button type="button" style={S.qtyBtnP} onClick={() => updateItemQty(sel.value, 1)}><Plus size={13} /></button>
                </div>
              </div>
            ))}
            <div style={S.typeFooterRow}>
              <button type="button" style={S.addAnotherBtn} onClick={addAnother}><Plus size={15} /> Add Another</button>
              <button
                type="button"
                style={{ ...S.continueBtn, ...S.typeFooterBtn, ...(selectedItems.length === 0 ? S.continueBtnDisabled : {}) }}
                disabled={selectedItems.length === 0}
                onClick={confirmQuantity}
              >
                Continue ({totalQty}) <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 5: Material Option ──────────────────────────────────────── */}
        {step === "material" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>🛒 Who will provide the material?</h2>
            <p style={S.sectionSub}>Applies to all items in this booking</p>
            <div style={S.materialGrid}>
              <button type="button" style={{ ...S.materialCard, ...(materialOption === "clicknido" ? S.materialCardActive : {}) }} onClick={() => setMaterialOption("clicknido")}>
                {materialOption === "clicknido" ? <CheckCircle2 size={18} color="#16a34a" /> : <Circle size={18} color="#d1d5db" />}
                <div>
                  <div style={S.materialTitle}>ClickNido Material</div>
                  <div style={S.materialSub}>ClickNido supplies the switch/socket — Price = Labour + Material</div>
                </div>
              </button>
              <button type="button" style={{ ...S.materialCard, ...(materialOption === "customer" ? S.materialCardActive : {}) }} onClick={() => setMaterialOption("customer")}>
                {materialOption === "customer" ? <CheckCircle2 size={18} color="#16a34a" /> : <Circle size={18} color="#d1d5db" />}
                <div>
                  <div style={S.materialTitle}>Customer Material</div>
                  <div style={S.materialSub}>I already have the switch/socket — Price = Labour Only</div>
                </div>
              </button>
            </div>
            {materialOption === "customer" && (
              <div style={S.materialNote}>
                <AlertTriangle size={13} /> The electrician will verify your material before installation — unsafe, damaged or incompatible material won't be installed.
              </div>
            )}
            <button type="button" style={S.continueBtn} onClick={() => setStep("photo")}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── STEP 6: Photo ────────────────────────────────────────────────── */}
        {step === "photo" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>📷 Add Photo</h2>
            <p style={S.sectionSub}>Optional — helps the electrician come prepared</p>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={S.hiddenInput} onChange={handlePhotoFile(setPhotoDataUrl)} />
            <input ref={photoInputRef} type="file" accept="image/*" style={S.hiddenInput} onChange={handlePhotoFile(setPhotoDataUrl)} />
            {photoDataUrl ? (
              <div style={S.photoPreviewRow}>
                <img src={photoDataUrl} alt="Attached" style={S.photoPreview} />
                <button type="button" style={S.addedRemoveBtn} onClick={() => setPhotoDataUrl("")}><X size={13} /></button>
              </div>
            ) : (
              <div style={S.photoBtnRow}>
                <button type="button" style={S.photoBtn} onClick={() => cameraInputRef.current?.click()}><Camera size={16} /> Take Photo</button>
                <button type="button" style={S.photoBtn} onClick={() => photoInputRef.current?.click()}><Upload size={16} /> Upload Photo</button>
              </div>
            )}

            <button type="button" style={S.continueBtn} onClick={afterPhotoContinue}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── STEP: Consultation — problem, description & photo ───────────── */}
        {step === "problem" && (
          <div style={S.card}>
            <div style={S.consultationIconWrap}><Waves size={26} color="#16a34a" /></div>
            <h2 style={S.sectionTitleCenter}>💬 What's the problem or requirement?</h2>
            <p style={S.sectionSubCenter}>Tell us what's going on — the electrician inspects and advises after arrival. No need to know repair/replacement, exact rating or wiring.</p>
            <div style={S.workTypeGrid}>
              {(service.problems || []).map((p) => {
                const Icon = PROBLEM_ICONS[p.key] || HelpCircle;
                return (
                  <button
                    key={p.key} type="button"
                    style={{ ...S.workTypeCard, ...(problemKey === p.key ? S.workTypeCardActive : {}) }}
                    onClick={() => setProblemKey(p.key)}
                  >
                    <div style={S.workTypeIconWrap}><Icon size={18} color="#16a34a" /></div>
                    <div style={S.workTypeLabel}>{p.label}</div>
                    {problemKey === p.key && <CheckCircle2 size={16} color="#16a34a" />}
                  </button>
                );
              })}
            </div>

            <h2 style={{ ...S.sectionTitle, marginTop: "22px" }}>Describe it (optional)</h2>
            <textarea
              style={S.textarea}
              value={applianceDetails}
              onChange={(e) => setApplianceDetails(e.target.value)}
              placeholder="E.g. The socket near the kitchen sink sparks sometimes."
              rows={3}
            />

            <h2 style={S.sectionTitle}>📷 Add Photo</h2>
            <p style={S.sectionSub}>Optional — helps the electrician come prepared</p>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={S.hiddenInput} onChange={handlePhotoFile(setPhotoDataUrl)} />
            <input ref={photoInputRef} type="file" accept="image/*" style={S.hiddenInput} onChange={handlePhotoFile(setPhotoDataUrl)} />
            {photoDataUrl ? (
              <div style={S.photoPreviewRow}>
                <img src={photoDataUrl} alt="Attached" style={S.photoPreview} />
                <button type="button" style={S.addedRemoveBtn} onClick={() => setPhotoDataUrl("")}><X size={13} /></button>
              </div>
            ) : (
              <div style={S.photoBtnRow}>
                <button type="button" style={S.photoBtn} onClick={() => cameraInputRef.current?.click()}><Camera size={16} /> Take Photo</button>
                <button type="button" style={S.photoBtn} onClick={() => photoInputRef.current?.click()}><Upload size={16} /> Upload Photo</button>
              </div>
            )}

            <button type="button" style={S.continueBtn} onClick={() => setStep("mode")}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── STEP 8: Instant / Schedule ───────────────────────────────────── */}
        {step === "mode" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>When do you need it?</h2>
            <p style={S.sectionSub}>Based on your selection, here's what's available</p>
            <div style={S.workTypeGrid}>
              {instantEligible && (
                <button type="button" style={S.workTypeCard} onClick={() => chooseBookingMode("instant")}>
                  <div style={S.workTypeIconWrap}><Zap size={20} color="#16a34a" /></div>
                  <div style={S.workTypeBody}>
                    <div style={S.workTypeLabel}>Instant</div>
                    <div style={S.workTypeDesc}>A technician is dispatched right away</div>
                  </div>
                  <ArrowRight size={16} color="#9ca3af" />
                </button>
              )}
              <button type="button" style={S.workTypeCard} onClick={() => chooseBookingMode("schedule")}>
                <div style={S.workTypeIconWrap}><CalendarDays size={20} color="#16a34a" /></div>
                <div style={S.workTypeBody}>
                  <div style={S.workTypeLabel}>Schedule</div>
                  <div style={S.workTypeDesc}>Pick a date &amp; 2-hour time slot</div>
                </div>
                <ArrowRight size={16} color="#9ca3af" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 9/10: Date & Time / Checkout ────────────────────────────── */}
        {step === "schedule" && (
          <>
            <div style={S.summaryBar}>
              <div style={S.summaryBody}>
                <div style={S.summaryTitle}>
                  {bookingMode === "requirement" ? "Requirement / Inspection Visit" : isConsultation ? "Electrical Consultation Booking" : "Switch & Socket Booking Summary"}
                </div>
                <div style={S.summaryChipRow}>
                  {isConsultation ? (
                    <span style={S.summaryChip}>{service.problems.find((p) => p.key === problemKey)?.label || "Consultation"}</span>
                  ) : (
                    selectedItems.map((sel) => (
                      <span key={sel.value} style={S.summaryChip}>{sel.label} × {sel.quantity}</span>
                    ))
                  )}
                  <span style={S.summaryDurationChip}>{hasHighLoadOrNotSure ? "Starting " : ""}₹{finalPayable.toFixed(2)}</span>
                </div>
                {bookingMode === "requirement" && (
                  <div style={S.materialNote}>
                    <AlertTriangle size={13} /> For Not Sure ratings, the electrician verifies wiring and electrical protection before installation. Additional work requires your approval.
                  </div>
                )}
              </div>
              <button type="button" style={S.summaryChangeBtn} onClick={() => setStep(hasHighLoadOrNotSure ? "photo" : "mode")}>
                Change
              </button>
            </div>

            <div style={S.scheduleHead}>
              <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
              <div style={S.legend}>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
              </div>
            </div>

            <div style={S.scheduleColumns}>
              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>1. Select Date</div>
                {loadingAvailability ? (
                  <div style={S.stateBox}>Loading…</div>
                ) : availabilityError ? (
                  <div style={S.errorBox}>{availabilityError}</div>
                ) : (
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button"
                          disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                <div style={S.slotGrid}>
                  {(activeDay?.slots || []).map((slot) => {
                    const state      = getSlotState(slot);
                    const selectable = state === "available";
                    const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                    const selected   = selectedTime === slot.time;
                    return (
                      <button
                        key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        style={{
                          ...S.slotBtn,
                          ...(selected ? S.slotBtnActive : {}),
                          ...(state === "booked" ? S.slotBtnBooked : {}),
                          ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                        }}
                        onClick={() => selectable && pickTime(slot.time, slot._id)}
                      >
                        <div style={S.slotTimeRow}>
                          <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                          {selected && <CheckCircle2 size={13} color="#fff" />}
                        </div>
                        <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                          {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.breakdownBox}>
              {isConsultation ? (
                <div style={S.breakdownRow}><span>Consultation Charge</span><span>₹{consultationFee.toFixed(2)}</span></div>
              ) : (
                <>
                  {selectedItems.map((sel) => {
                    const amt = serviceAmountFor(sel);
                    return typeof sel.basePrice === "number" ? (
                      <div key={sel.value} style={S.breakdownRow}><span>{sel.label} × {sel.quantity}</span><span>₹{amt.toFixed(2)}</span></div>
                    ) : null;
                  })}
                  <div style={S.breakdownRow}><span>Service Amount</span><span>₹{totalServiceAmount.toFixed(2)}</span></div>
                </>
              )}
              <div style={S.breakdownRow}><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>Platform Fee 6%</span><span>₹{platformFee.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>GST 6%</span><span>₹{gst.toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>{hasHighLoadOrNotSure ? "Starting Price" : "Final Payable"}</span><span>₹{finalPayable.toFixed(2)}</span></div>
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!selectedTime || checkoutLoading ? S.continueBtnDisabled : {}) }}
              disabled={!selectedTime || checkoutLoading}
              onClick={handleCheckout}
            >
              {checkoutLoading ? "Proceeding..." : isUserLoggedIn() ? "Proceed to Payment" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "660px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionTitleCenter: { margin: "0 0 8px", fontSize: "19px", fontWeight: 800, color: "#111827", textAlign: "center" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },
  sectionSubCenter: { margin: "0 0 20px", fontSize: "13px", color: "#6b7280", textAlign: "center" },
  helperNote: { fontSize: "12px", color: "#9ca3af", marginTop: "10px" },

  workTypeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" },
  workTypeCard: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  workTypeCardActive: { borderColor: "#16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  workTypeIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  workTypeBody: { flex: 1, minWidth: 0 },
  workTypeLabel: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  workTypeDesc: { fontSize: "11.5px", color: "#9ca3af", marginTop: "2px" },

  typeNote: { fontSize: "11px", color: "#b45309", marginTop: "3px", fontWeight: 700 },
  typePrice: { fontSize: "12.5px", fontWeight: 700, color: "#16a34a", marginTop: "2px" },

  hiddenInput: { display: "none" },
  photoBtnRow: { display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" },
  photoBtn: { display: "inline-flex", alignItems: "center", gap: "8px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "11px 18px", borderRadius: "10px" },
  photoPreviewRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" },
  photoPreview: { width: "80px", height: "80px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e5e7eb" },

  textarea: { width: "100%", minHeight: "80px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontFamily: "inherit", fontSize: "13px", resize: "vertical", marginBottom: "16px", boxSizing: "border-box" },

  qtyLineRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" },
  qtyLineLabel: { fontSize: "13.5px", fontWeight: 700, color: "#374151" },
  qtyRow: { display: "flex", alignItems: "center", gap: "10px" },
  qtyBtn: { width: "28px", height: "28px", borderRadius: "50%", border: "1px solid #e4e7ef", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", flexShrink: 0 },
  qtyBtnP: { width: "28px", height: "28px", borderRadius: "50%", border: "none", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 },
  qtyText: { minWidth: "18px", textAlign: "center", fontSize: "13px", fontWeight: 700, color: "#111827" },

  addedBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "14px 16px", marginTop: "18px" },
  addedTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  addedRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "#374151", padding: "5px 0" },
  addedRemoveBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  typeFooterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginTop: "10px" },
  addAnotherBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "10px 16px", borderRadius: "10px" },
  typeFooterBtn: { width: "auto", padding: "13px 24px", marginTop: 0 },

  materialGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "12px" },
  materialCard: { display: "flex", alignItems: "flex-start", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  materialCardActive: { borderColor: "#16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  materialTitle: { fontSize: "14px", fontWeight: 800, color: "#111827" },
  materialSub: { fontSize: "12px", color: "#6b7280", marginTop: "2px" },
  materialNote: { display: "flex", alignItems: "flex-start", gap: "6px", fontSize: "12px", color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "10px 12px", marginBottom: "16px", lineHeight: 1.4 },

  consultationIconWrap: { width: "56px", height: "56px", borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" },
  consultationFeeChip: { display: "block", width: "fit-content", margin: "0 auto 14px", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontWeight: 800, fontSize: "14px", padding: "8px 18px", borderRadius: "999px" },

  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
  summaryBody: { minWidth: 0, flex: 1 },
  summaryTitle: { fontSize: "15px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
};

export default SwitchSocketBookingPage;
